import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, CallLog, Campaign, RuntimeSnap } from "../lib/api";

export function CampaignDetailPage({ csrf }: { csrf: string }) {
  const { id = "" } = useParams();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [runtime, setRuntime] = useState<RuntimeSnap | null>(null);
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const data = await api.getCampaign(id);
    setCampaign(data.campaign);
    setRuntime(data.runtime);
    setCalls(data.calls);
  }, [id]);

  useEffect(() => {
    refresh().catch((e) => setError(e.message));
    const t = setInterval(() => {
      refresh().catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [refresh]);

  async function act(action: "start" | "pause" | "stop") {
    if (action === "start") {
      const route = campaign?.routingSnapshot as Record<string, unknown> | null | undefined;
      const calling = route?.outboundNumber || campaign?.selectedOutboundNumberId || "not selected";
      const destination = route?.receivingMethod || campaign?.selectedReceivingMethod || "SIP";
      if (!window.confirm(`CAMPAIGN ROUTING\n\nCalling Number: ${calling}\nPress-1 Destination: ${destination}\n\nSTART CAMPAIGN`)) return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api.campaignAction(id, action, csrf);
      setCampaign(res.campaign);
      setRuntime(res.runtime);
      await refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  if (!campaign) {
    return (
      <div>
        <p className="muted">Loading campaign…</p>
        {error ? <div className="alert error">{error}</div> : null}
      </div>
    );
  }

  const s = campaign.stats || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <p className="muted">
            <Link to="/campaigns">Campaigns</Link> / {campaign.name}
          </p>
          <h1>{campaign.name}</h1>
          <p>
            Status <span className={`badge ${campaign.status}`}>{campaign.status}</span>
            {runtime ? (
              <>
                {" "}
                · live channels {runtime.activeCalls}/{runtime.maxConcurrent} · queue {runtime.queued}
              </>
            ) : null}
          </p>
        </div>
        <div className="row-actions">
          <button className="btn success" type="button" disabled={busy} onClick={() => act("start")}>
            Start dialing
          </button>
          <button className="btn ghost" type="button" disabled={busy} onClick={() => act("pause")}>
            Pause
          </button>
          <button className="btn danger" type="button" disabled={busy} onClick={() => act("stop")}>
            Stop
          </button>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="grid-stats">
        <div className="stat-card">
          <div className="label">Total</div>
          <div className="value">{s.total ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="label">Pending</div>
          <div className="value">{s.pending ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="label">Dialing / live</div>
          <div className="value">{s.dialing ?? runtime?.activeCalls ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="label">Answered</div>
          <div className="value">{s.answered ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="label">Completed</div>
          <div className="value">{s.completed ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="label">Failed / NA / Busy</div>
          <div className="value">
            {(s.failed ?? 0) + (s.noAnswer ?? 0) + (s.busy ?? 0)}
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Campaign routing</h2>
        <p><strong>Owner:</strong> {campaign.ownerAgentId || "Assigned when dispatched"}</p>
        <p><strong>Calling number:</strong> {String(campaign.routingSnapshot?.outboundNumber || campaign.selectedOutboundNumberId || "Not selected")}</p>
        <p><strong>Press-1 destination:</strong> {String(campaign.routingSnapshot?.receivingMethod || campaign.selectedReceivingMethod || "SIP")}</p>
        <p className="muted">{campaign.dispatchedAt ? `Locked at ${new Date(campaign.dispatchedAt).toLocaleString()}` : "Routing will be locked when the campaign starts."}</p>
      </div>

      <div className="card">
        <h2>Script</h2>
        <p style={{ whiteSpace: "pre-wrap" }}>{campaign.messageScript}</p>
        <p className="muted">
          Provider: {campaign.provider} · TTS: {campaign.ttsProvider} · Max concurrent:{" "}
          {campaign.maxConcurrent}
        </p>
      </div>

      <div className="card">
        <h2>Live call log</h2>
        <table>
          <thead>
            <tr>
              <th>Phone</th>
              <th>Status</th>
              <th>Delivered</th>
              <th>Error</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((c) => (
              <tr key={c.id}>
                <td className="mono">{c.phone}</td>
                <td>
                  <span className={`badge ${c.status}`}>{c.status}</span>
                </td>
                <td>{c.messageDelivered ? "yes" : "—"}</td>
                <td className="muted">{c.error || ""}</td>
                <td className="muted">{new Date(c.createdAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
